import React, { useEffect, useState, useCallback, useRef } from 'react';
import { dataQualityService } from '../../services/dataQualityService';
import CompletenessTrend from './CompletenessTrend';
import RunStepper from './RunStepper';
import RunKpiRow from './RunKpiRow';
import RunEntityBreakdown from './RunEntityBreakdown';
import { enrichStepLabel } from '../../lib/steps';

// A step can fail (e.g. people_whereabouts crashing on a Unicode/UTF-8 write) while
// the run it belongs to still finishes status='success' — cmd/pipelinecontrol never
// fails the header run over one step's error, because that would make the quality
// dashboard discard every OTHER step's real fills for the night (see
// cmd/pipelinecontrol/history_view.go's failedSteps doc). failed_steps
// (cmd/pipelinecontrol/history_view.go's derived field, parsed from
// lineage.run.summary.steps) is the only place that error reaches the client, and
// this is the nightly Cloud Run Job's own history row — the path that matters most,
// since nobody is watching a live SSE stream for a 01:00 job.
function RunFailureNotice({ failedSteps }) {
  if (!failedSteps || failedSteps.length === 0) return null;
  return (
    <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5" role="alert">
      <p className="text-xs font-semibold" style={{ color: '#dc2626' }}>
        {failedSteps.length === 1 ? "1 step didn't finish this run" : `${failedSteps.length} steps didn't finish this run`}
      </p>
      <ul className="mt-1.5 space-y-1">
        {failedSteps.map((s) => (
          <li key={s.name} className="text-xs" style={{ color: '#dc2626' }}>
            <span className="font-medium">{enrichStepLabel(s.name)}</span>: {s.err}
          </li>
        ))}
      </ul>
    </div>
  );
}

const PAGE = 25;

// History tab (Option A — Run Stepper): the trend for the arc over time, then a stepper
// to travel between runs. For the selected run we show its KPIs and per-entity/field
// composition bars, diffed against either the previous run or the ATS import.
export default function RunHistoryPanel() {
  const [runs, setRuns] = useState([]);
  const [total, setTotal] = useState(0);
  const [index, setIndex] = useState(0); // position in `runs` (newest-first); 0 = latest
  const [baseline, setBaseline] = useState('prev'); // 'prev' | 'import'
  const [detail, setDetail] = useState(null);
  const [loadingRuns, setLoadingRuns] = useState(true);
  const [error, setError] = useState(null);

  const mountedRef = useRef(true);
  const loadingRef = useRef(false);
  // Reset to true on setup, not only false on cleanup: React 18 StrictMode mounts →
  // unmounts → remounts in dev, and a cleanup-only guard would leave mountedRef false
  // for the remounted instance, so the in-flight fetch's result gets discarded.
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadRuns = useCallback((offset, append) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoadingRuns(true);
    dataQualityService
      .getHistory({ limit: PAGE, offset })
      .then((r) => {
        if (!mountedRef.current) return;
        setTotal(r.total || 0);
        setRuns((prev) => (append ? [...prev, ...(r.runs || [])] : r.runs || []));
        setError(null);
      })
      .catch((e) => { if (mountedRef.current) setError(e.message || 'Failed to load run history'); })
      .finally(() => { loadingRef.current = false; if (mountedRef.current) setLoadingRuns(false); });
  }, []);

  useEffect(() => { loadRuns(0, false); }, [loadRuns]);

  // Fetch the selected run's detail whenever the run or baseline changes. Keep the prior
  // detail on screen during a refetch so toggling the baseline doesn't flash a skeleton.
  const currentRunId = runs[index] && runs[index].run_id;
  useEffect(() => {
    if (!currentRunId) { setDetail(null); return undefined; }
    let live = true;
    dataQualityService
      .getRunHistoryDetail(currentRunId, { baseline })
      .then((d) => { if (live) { setDetail(d); setError(null); } })
      .catch((e) => { if (live) setError(e.message || 'Failed to load run detail'); });
    return () => { live = false; };
  }, [currentRunId, baseline]);

  const olderCount = total - runs.length;

  return (
    <section className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" aria-label="run history">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">How your data improved over time</h3>
        <p className="text-sm text-gray-500 mt-1">Step between runs to see what each one added — completeness, and what it still can’t enrich.</p>
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      {loadingRuns && runs.length === 0 ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-12 bg-gray-100 rounded animate-pulse motion-reduce:animate-none" />)}</div>
      ) : runs.length === 0 ? (
        <p className="text-sm text-gray-500">History appears after your first import.</p>
      ) : (
        <>
          <div className="mb-5"><CompletenessTrend runs={runs} /></div>

          <RunStepper
            runs={runs}
            index={index}
            baseline={baseline}
            onPick={(i) => setIndex(Math.max(0, Math.min(runs.length - 1, i)))}
            onBaseline={setBaseline}
          />

          <RunFailureNotice failedSteps={runs[index] && runs[index].failed_steps} />

          {olderCount > 0 && (
            <button type="button" onClick={() => loadRuns(runs.length, true)} disabled={loadingRuns}
              className="mt-2 text-xs text-[#007A6B] hover:underline disabled:text-gray-400">
              {loadingRuns ? 'Loading…' : `Load ${olderCount} older run${olderCount === 1 ? '' : 's'}`}
            </button>
          )}

          {detail ? (
            <>
              <RunKpiRow overall={detail.overall} baseline={detail.baseline} />
              {detail.overall ? (
                <RunEntityBreakdown entities={detail.entities || []} baseline={detail.baseline} />
              ) : (
                <p className="text-sm text-gray-500 mt-2">No quality data for this run.</p>
              )}
            </>
          ) : (
            <div className="mt-3 space-y-2">{[0, 1].map((i) => <div key={i} className="h-16 bg-gray-100 rounded animate-pulse motion-reduce:animate-none" />)}</div>
          )}
        </>
      )}
    </section>
  );
}
